const getUniqueLocations = (locations = []) => {
  if (!Array.isArray(locations) || locations?.length < 1) return [];
  const uniqueLocations = [];
  const locationIds = new Set();

  for (let loc of locations) {
    if (!locationIds.has(loc.id)) {
      locationIds.add(loc.id);
      uniqueLocations.push(loc);
    }
  }
  return uniqueLocations;
};

const locationsToLocationZones = (locations = []) => {
  if (!Array.isArray(locations) || locations?.length < 1) return [];
  const locationFloor = [];

  const locationsWithFloor = locations.filter((loc) => loc?.floors);

  if (locationsWithFloor.length < 1) return [];

  for (let loc of locationsWithFloor) {
    const floors = getAllFloorsFromLocation(loc);
    locationFloor.push({ ...loc, floors });
  }
  
  return locationFloor;
};

const getAllFloorsFromLocation = (location) => {
  if (!location) return [];

  // 1. Get a copy of the floor objects using map and the spread operator
  const allFloors = Object.values(location?.floors).map(floor => ({...floor})); // <-- FIX: Create a shallow copy of the floor object

  allFloors.forEach((floor) => {
    if (floor?.zones) {
      const zones = getAllZonesFromFloor(floor);
      floor.zones = zones; // Now this modifies the *copy*, not the original
    }
  });

  return allFloors;
};

const getAllZonesFromFloor = (floor) => {
  if (!floor) return [];
  return Object.values(floor?.zones || {});
};

const getLocationZoneList = (locations = []) => {
  if (!Array.isArray(locations) || locations?.length < 1) return [];
  const locationZones = [];
  const uniqueIds = [];

  for (let loc of locations) {
    for (let floor of loc?.floors || []) {
      for (let zone of floor?.zones || []) {
        if (zone?.zone_name) {
          const locName = [loc.name];
          if (floor?.name) {
            locName.push(floor.name);
          }
          locName.push(zone.zone_name);
          const id = `${loc.id}=${floor.id}=${zone.id}`;
          if (!uniqueIds.includes(id)) {
            uniqueIds.push(id);
            locationZones.push({
              location: loc,
              id: id,
              name: `${locName.join(" - ")}`,
            });
          }
        }
      }
    }
  }
 
  return locationZones;
};

/**
 * Converts the array of site location objects into a new array where
 * 'floors' and 'zones' are represented as arrays of objects, instead of nested objects/maps.
 * It maintains the overall structure of the original location objects while transforming the nested data.
 * * @param {Array<Object>} locationsArray - The array of site location objects.
 * @returns {Array<Object>} An array of location objects with 'floors' and 'zones' as arrays.
 */
function convertToArrays(locationsArray) {
  return locationsArray.map(location => {
    // Create a shallow copy of the location object
    const newLocation = { ...location
    };

    // Process the 'floors' property
    if (newLocation.floors && typeof newLocation.floors === 'object') {
      const floorsMap = newLocation.floors;
      const floorsArray = [];

      for (const floorId in floorsMap) {
        if (Object.prototype.hasOwnProperty.call(floorsMap, floorId)) {
          const floor = floorsMap[floorId];

          // Create a shallow copy of the floor object
          const newFloor = { ...floor
          };

          // Process the 'zones' property within the floor
          if (newFloor.zones && typeof newFloor.zones === 'object') {
            const zonesMap = newFloor.zones;
            const zonesArray = [];

            for (const zoneId in zonesMap) {
              if (Object.prototype.hasOwnProperty.call(zonesMap, zoneId)) {
                // Create a copy of the zone object
                zonesArray.push({ ...zonesMap[zoneId]
                });
              }
            }
            // Replace the zones map with the zones array
            newFloor.zones = zonesArray;
          }

          // Add the processed floor object to the floors array
          floorsArray.push(newFloor);
        }
      }
      // Replace the floors map with the floors array
      newLocation.floors = floorsArray;
    }

    return newLocation;
  });
}

const isAssetActiveInZone = (asset) => {
  const freqStr = asset?.profile_frequencies?.sensordatafrequency;
  const lastUpdated = asset?.updatedat;

  if (!freqStr || !lastUpdated) {
    return false;
  }

  const parts = String(freqStr).trim().split(/\s+/);
  if (parts.length !== 2) {
    return false;
  }

  const freqValue = parseFloat(parts[0]);
  const freqUnit = parts[1].toLowerCase();

  if (isNaN(freqValue)) {
    return false;
  }

  let freqMs;
  if (freqUnit.startsWith("s")) {
    freqMs = freqValue * 1000;
  } else if (freqUnit.startsWith("m")) {
    freqMs = freqValue * 60 * 1000;
  } else if (freqUnit.startsWith("h")) {
    freqMs = freqValue * 60 * 60 * 1000;
  } else {
    return false;
  }

  const now = Date.now();
  const lastUpdatedMs = new Date(lastUpdated).getTime();
  const timeDiffMs = now - lastUpdatedMs;
  const result = (2 * freqMs) < timeDiffMs;

  return result;
};

export {
  getUniqueLocations,
  locationsToLocationZones,
  getAllFloorsFromLocation,
  getAllZonesFromFloor,
  getLocationZoneList,
  convertToArrays,
  isAssetActiveInZone
};
